import { useState, useEffect, useRef } from 'react';
import { Dimensions, Platform, AppState } from 'react-native';

/**
 * Hook to detect if a foldable device is currently in the unfolded/opened state
 * Returns false when the device is folded (closed) or not a foldable
 */
export function useFoldable() {
  const [isFoldableOpen, setIsFoldableOpen] = useState(false);
  const previousDimensions = useRef<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (Platform.OS !== 'android') {
      return;
    }

    const checkFoldableOpen = ({ window }: { window: any }) => {
      const { width, height } = window || Dimensions.get('window');
      const minDimension = Math.min(width, height);

      // Check if dimensions actually changed
      const dimensionsChanged = !previousDimensions.current ||
        previousDimensions.current.width !== width ||
        previousDimensions.current.height !== height;

      previousDimensions.current = { width, height };

      // When a foldable is unfolded/opened:
      // - It has tablet-like dimensions with a WIDE minimum dimension (> 650dp)
      // - Example: Galaxy Z Fold unfolded is ~842x701 or ~1768x2208px
      //
      // When folded/closed or regular phone:
      // - Narrow width even if tall (< 500dp typically)
      // - Example: Galaxy Z Fold folded is ~373x816px
      // - Regular phones: ~360-420px width
      //
      // Using 650dp threshold to be more conservative and avoid false positives
      const isUnfolded = minDimension > 650;

      setIsFoldableOpen(prev => {
        return isUnfolded;
      });
    };

    // Initial check with just window object
    checkFoldableOpen({ window: Dimensions.get('window') });

    // Listen for dimension changes
    const subscription = Dimensions.addEventListener('change', checkFoldableOpen);

    // Also listen for app state changes (foreground/background)
    const appStateSubscription = AppState.addEventListener('change', (nextAppState) => {
      if (nextAppState === 'active') {
        checkFoldableOpen({ window: Dimensions.get('window') });
      }
    });

    // FALLBACK: Poll dimensions every 500ms to catch fold events that don't fire Dimensions.change
    // This is especially important for OPEN → CLOSED transitions which seem to not fire reliably
    const pollInterval = setInterval(() => {
      const current = Dimensions.get('window');
      if (previousDimensions.current) {
        const changed =
          previousDimensions.current.width !== current.width ||
          previousDimensions.current.height !== current.height;

        if (changed) {
          checkFoldableOpen({ window: current });
        }
      }
    }, 500);

    return () => {
      subscription?.remove();
      appStateSubscription?.remove();
      clearInterval(pollInterval);
    };
  }, []);

  return isFoldableOpen;
}
